import axios from "axios";
import Swal from "sweetalert2";

// 🌐 Base URL dinámica (funciona local + prod + subdominio)
const API_URL = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: API_URL.trim(),
  timeout: 15000, // ⏱ evita cuelgues eternos
});

// ─────────────────────────────────────────────────────────────
// 🔐 SESSION CONFIG
// ─────────────────────────────────────────────────────────────
const SESSION_KEYS = [
  "token",
  "usuario",
  "empresa_contable_id",
  "empresa_contable_nombre",
  "tenant_id",
];

let isLoggingOut = false;

// ─────────────────────────────────────────────────────────────
// 🔎 Decode JWT (seguro)
// ─────────────────────────────────────────────────────────────
const getTokenExp = (token) => {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload.exp || null;
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────────────────────
// 🚪 LOGOUT CENTRALIZADO
// ─────────────────────────────────────────────────────────────
const logout = (mensaje = "Tu sesión ha expirado") => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  SESSION_KEYS.forEach((k) => localStorage.removeItem(k));

  // Notificar a App.js para que llame setUsuario(null) de inmediato
  // → el Navbar y las rutas protegidas desmontan antes que el Swal cierre
  window.dispatchEvent(new CustomEvent("efit:logout"));

  Swal.fire({
    icon: "warning",
    title: "Sesión finalizada",
    text: mensaje,
    timer: 1600,
    showConfirmButton: false,
    allowOutsideClick: false,
  }).then(() => {
    isLoggingOut = false;
    window.location.href = "/login";
  });
};

// ─────────────────────────────────────────────────────────────
// 📤 REQUEST INTERCEPTOR
// ─────────────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      const exp = getTokenExp(token);

      // ⛔ Token expirado antes de salir
      if (exp && Date.now() / 1000 > exp) {
        logout("Tu sesión ha expirado. Inicia sesión nuevamente.");
        return Promise.reject(new Error("Token expirado"));
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    // 🏢 Multi-tenant
    const empresaId = localStorage.getItem("empresa_contable_id");
    if (empresaId) {
      config.headers["x-empresa-id"] = empresaId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ─────────────────────────────────────────────────────────────
// 📥 RESPONSE INTERCEPTOR
// ─────────────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,

  (error) => {
    // 🔴 Timeout / backend caído
    if (error.code === "ECONNABORTED") {
      console.error("⏱ Timeout API:", error.config?.url);
      return Promise.reject(error);
    }

    // 🔴 Sin respuesta (nginx / server caído)
    if (!error.response) {
      console.error("🌐 Error de red / backend no responde");
      return Promise.reject(error);
    }

    const status = error.response.status;
    const data = error.response.data;

    // 🔐 401 → sesión inválida
    if (status === 401) {
      logout("Sesión inválida o expirada");
      return Promise.reject(error);
    }

    // 🚫 403 → reglas de negocio
    if (status === 403) {
      if (data?.error === "trial_vencido") {
        localStorage.setItem(
          "trial_vencido",
          data.mensaje || "Tu período de prueba ha vencido."
        );
        window.location.href = "/";
        return Promise.reject(error);
      }

      if (data?.error === "empresa_inactiva") {
        localStorage.setItem(
          "empresa_inactiva",
          data.mensaje || "Empresa inactiva."
        );
        window.location.href = "/";
        return Promise.reject(error);
      }
    }

    // 🧠 Otros errores siguen su flujo normal
    return Promise.reject(error);
  }
);

export default api;